/**
 * useUrlViewSync — keeps component view state and URL search params in sync.
 *
 * - Hydrates from the URL on mount and when the user navigates with back/forward.
 * - Writes non-default state back to the URL (replace, so filter tweaks don't
 *   flood the history stack).
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { paramsEqual } from '../utils/urlState.js';

export default function useUrlViewSync({ parse, serialize, apply, deps }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hydrated = useRef(false);
  const skipWrite = useRef(false);

  // URL → state (initial load + browser navigation)
  useEffect(() => {
    skipWrite.current = true;
    apply(parse(searchParams));
    hydrated.current = true;
    const t = requestAnimationFrame(() => { skipWrite.current = false; });
    return () => cancelAnimationFrame(t);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // state → URL
  useEffect(() => {
    if (!hydrated.current || skipWrite.current) return;
    const next = serialize();
    if (!paramsEqual(next, searchParams)) {
      setSearchParams(next, { replace: true });
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
